import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  useReverseVendorBill,
  useListPostingExceptions,
  getListVendorBillsQueryKey,
  getGetVendorBillQueryKey,
} from "@workspace/api-client-react";
import type { VendorBillDetail } from "@workspace/api-client-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/context/auth-context";
import { inr } from "@/lib/format";
import { venId } from "@/lib/display-id";
import { TriangleAlert } from "lucide-react";
import { STATUS_LABEL, billTreatmentLabel } from "@/components/expenses/bill-list";

export interface BillDetailSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  bill: VendorBillDetail | null;
  onRecordPayment?: (bill: VendorBillDetail) => void;
}

export function BillDetailSheet({ open, onOpenChange, bill, onRecordPayment }: BillDetailSheetProps) {
  const { toast } = useToast();
  const { user } = useAuth();
  const qc = useQueryClient();
  const reverseMut = useReverseVendorBill();
  // FR-11 — a quarantined posting must be visible from the one screen an
  // operator is actually looking at, not only from the /ledger Quarantine
  // tab. Matched by sourceEventId (the bill's own id for an approve-time
  // quarantine, or one of its payment ids for a payment-time quarantine).
  const { data: exceptions } = useListPostingExceptions({ resolved: false });

  const [reverseOpen, setReverseOpen] = useState(false);
  const [reverseReason, setReverseReason] = useState("");

  if (!bill) return null;

  const isAdmin = user?.role === "super_admin" || user?.role === "admin";
  const relevantIds = new Set<number>([bill.id, ...bill.payments.map((p) => p.id)]);
  const quarantineHit = (exceptions ?? []).find(
    (e) => e.sourceModule === "expense" && e.sourceEventId != null && relevantIds.has(e.sourceEventId),
  );

  const canReverse = isAdmin && bill.journalEntryId != null && bill.status !== "void";

  async function submitReverse() {
    if (!bill || !reverseReason.trim()) {
      toast({ title: "A reason is required.", variant: "destructive" });
      return;
    }
    try {
      await reverseMut.mutateAsync({ id: bill.id, data: { reason: reverseReason.trim() } });
      qc.invalidateQueries({ queryKey: getListVendorBillsQueryKey() });
      qc.invalidateQueries({ queryKey: getGetVendorBillQueryKey(bill.id) });
      toast({ title: `Bill #${bill.id} reversed.` });
      setReverseOpen(false);
      setReverseReason("");
    } catch (err) {
      toast({ title: err instanceof Error ? err.message : "Could not reverse.", variant: "destructive" });
    }
  }

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent side="right" className="w-full sm:max-w-[720px] flex flex-col overflow-y-auto">
          <SheetHeader>
            <SheetTitle>
              Bill #{bill.id} — {bill.vendorName}
            </SheetTitle>
          </SheetHeader>
          <div className="flex-1 space-y-5 py-4">
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <p className="text-xs text-muted-foreground">Vendor</p>
                <p>
                  {venId(bill.vendorId)} · {bill.vendorName}
                </p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Bill number</p>
                <p>{bill.billNumber}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Bill date</p>
                <p>{bill.billDate.slice(0, 10)}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Due date</p>
                <p>{bill.dueDate ? bill.dueDate.slice(0, 10) : "—"}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Treatment</p>
                <Badge variant="outline">{billTreatmentLabel(bill)}</Badge>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Status</p>
                <Badge variant="secondary">{STATUS_LABEL[bill.status] ?? bill.status}</Badge>
              </div>
            </div>

            {quarantineHit && (
              <Alert variant="destructive">
                <TriangleAlert className="h-4 w-4" />
                <AlertTitle>Posting quarantined</AlertTitle>
                <AlertDescription>
                  {quarantineHit.reason} — resolve this from the Ledger → Quarantine tab; nothing was silently dropped.
                </AlertDescription>
              </Alert>
            )}

            <div>
              <p className="text-xs font-semibold mb-1">Lines</p>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Description</TableHead>
                    <TableHead>Account</TableHead>
                    <TableHead>Cost centre</TableHead>
                    <TableHead className="text-right">Taxable</TableHead>
                    <TableHead className="text-right">GST</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {bill.lines.map((l) => (
                    <TableRow key={l.id}>
                      <TableCell>{l.description}</TableCell>
                      <TableCell className="text-xs">
                        {l.expenseAccountCode} · {l.expenseAccountName}
                      </TableCell>
                      <TableCell className="text-xs">{l.costCentreName}</TableCell>
                      <TableCell className="text-right font-mono tabular-nums">{inr(l.taxableAmount)}</TableCell>
                      <TableCell className="text-right font-mono tabular-nums">
                        {inr(l.gstAmount)} ({l.gstRate}%)
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <div className="flex justify-end gap-6 text-sm font-mono tabular-nums pr-2 mt-1">
                <span>Taxable {inr(bill.taxableTotal)}</span>
                <span>GST {inr(bill.gstTotal)}</span>
                <span className="font-semibold">Gross {inr(bill.grossTotal)}</span>
              </div>
              {bill.taxTreatment === "itc_blocked" && (
                <p className="text-[11px] text-muted-foreground mt-1">
                  GST is not claimable on this purchase (§17(5)); the full GST-inclusive amount is charged to expense.
                </p>
              )}
            </div>

            <div>
              <div className="flex items-center justify-between mb-1">
                <p className="text-xs font-semibold">Payments</p>
                {(bill.status === "approved" || bill.status === "partially_paid") && onRecordPayment && (
                  <Button size="sm" onClick={() => onRecordPayment(bill)}>
                    Record payment
                  </Button>
                )}
              </div>
              {bill.payments.length === 0 ? (
                <p className="text-sm text-muted-foreground">No payments recorded yet.</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead className="text-right">Gross</TableHead>
                      <TableHead className="text-right">TDS</TableHead>
                      <TableHead>Paid from</TableHead>
                      <TableHead>Method</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {bill.payments.map((p) => (
                      <TableRow key={p.id}>
                        <TableCell className="text-sm">{p.paymentDate.slice(0, 10)}</TableCell>
                        <TableCell className="text-right font-mono tabular-nums">{inr(p.grossAmount)}</TableCell>
                        <TableCell className="text-right font-mono tabular-nums">{p.tdsAmount > 0 ? inr(p.tdsAmount) : "—"}</TableCell>
                        <TableCell className="text-sm">{p.paymentAccountName}</TableCell>
                        <TableCell className="text-sm">{p.paymentMethod ?? "—"}</TableCell>
                        <TableCell>
                          {p.journalEntryId != null ? (
                            <Badge variant="secondary">Posted</Badge>
                          ) : (
                            <Badge variant="destructive">Quarantined</Badge>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </div>

            <div>
              <div className="flex items-center justify-between mb-1">
                <p className="text-xs font-semibold">Journal entry</p>
                {canReverse && (
                  <Button size="sm" variant="outline" className="text-destructive" onClick={() => setReverseOpen(true)}>
                    Reverse
                  </Button>
                )}
              </div>
              {!bill.journalEntry ? (
                <p className="text-sm text-muted-foreground">
                  {bill.awaitingPosting
                    ? "Migrated bill — awaiting the open-bill backfill run before AP is posted to the ledger."
                    : quarantineHit
                      ? "Not posted — see the quarantine notice above."
                      : "Not posted yet."}
                </p>
              ) : (
                <div className="space-y-1">
                  <p className="text-xs text-muted-foreground">
                    {bill.journalEntry.entryNo} · {bill.journalEntry.entryDate.slice(0, 10)}
                    {bill.journalEntry.reversesEntryId != null ? " · reverses a prior entry" : ""}
                  </p>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Account</TableHead>
                        <TableHead>Cost centre</TableHead>
                        <TableHead className="text-right">Debit</TableHead>
                        <TableHead className="text-right">Credit</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {bill.journalEntry.lines.map((l) => (
                        <TableRow key={l.id}>
                          <TableCell className="text-xs">
                            {l.accountCode} · {l.accountName}
                          </TableCell>
                          <TableCell className="text-xs">{l.costCentreName}</TableCell>
                          <TableCell className="text-right font-mono tabular-nums">{l.debit > 0 ? inr(l.debit) : ""}</TableCell>
                          <TableCell className="text-right font-mono tabular-nums">{l.credit > 0 ? inr(l.credit) : ""}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </div>
          </div>
        </SheetContent>
      </Sheet>

      <Dialog open={reverseOpen} onOpenChange={setReverseOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Reverse bill #{bill.id}?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            This posts a new, balanced reversing entry referencing the original — nothing is edited or deleted (FR-03).
          </p>
          <div>
            <label className="text-xs font-semibold">
              Reason <span className="text-destructive">*</span>
            </label>
            <Textarea value={reverseReason} onChange={(e) => setReverseReason(e.target.value)} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReverseOpen(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={submitReverse} disabled={reverseMut.isPending}>
              {reverseMut.isPending ? "Reversing…" : "Reverse"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
